/**
 * API documentation for the Order Confirmation endpoints (#1909), shown only to tenants that have
 * Order Confirmation enabled.
 *
 * `GET /order/order/{title}/status`, `POST /order/order/{title}/complete` and
 * `POST /order/order/{title}/copy` are rules keyed on an action, served by the generic
 * `/{namespace}/{relation}/{id}/{action}` route. The generated paths come from the use case's
 * metadata, which describes relations, not actions, so these three never appeared in a tenant's
 * API documentation — the integrator who needs them most had nothing to read.
 *
 * They are added here, with two conditions, so the documentation matches what the API will do for
 * the reader:
 *
 * - The tenant has `orderConfirmation_enabled`. That is the setting the rules engine gates these
 *   endpoints on (`hasFeatureFlag` reads `activeTenant.settings`, truthily), so a tenant without it
 *   would be documenting endpoints that answer as if they did not exist.
 * - The document already carries `order/order` for this reader. That inherits every rule which
 *   decided that — the role's access, `hideApiDocs`, a relation the use case does not have — rather
 *   than restating them, and puts these operations under the same tag as the rest of the order API.
 *
 * Adding them does not start validating requests to them. Request validation reads this same
 * document, but looks paths up by exact match against the concrete request path
 * (validationsFromOpenApi.js), and `/order/order/ORD-001/complete` never equals the templated
 * `/order/order/{title}/complete`.
 *
 * Kept in step with docs/product/prds/Order_confirmation/api-contract.md, which is the long form.
 */

const ORDER_BASE_PATH = '/order/order';

const TITLE_PARAMETER = Object.freeze({
    name: 'title',
    in: 'path',
    required: true,
    description: 'The order title, as the WMS sent it on create — for example ORD-001.',
    schema: { type: 'string' }
});

const NULLABLE_STRING = { type: ['string', 'null'] };
const NULLABLE_DATE_TIME = { type: ['string', 'null'], format: 'date-time' };
const COUNT = { type: 'integer', minimum: 0 };

const STATUS_RESPONSE = {
    type: 'object',
    properties: {
        title: NULLABLE_STRING,
        status: { ...NULLABLE_STRING, description: 'Order status title, e.g. "In Progress".' },
        fulfillmentStatus: NULLABLE_STRING,
        location: NULLABLE_STRING,
        expected: { ...COUNT, description: 'Units expected, summed across lines.' },
        scanned: { ...COUNT, description: 'Units verified against an expected line.' },
        overScanned: { ...COUNT, description: 'Units verified that no line expected.' },
        remaining: COUNT,
        totalItems: COUNT,
        progress: { type: 'integer', minimum: 0, maximum: 100 },
        closedTime: NULLABLE_DATE_TIME,
        closedReason: NULLABLE_STRING,
        lastModified: {
            ...NULLABLE_DATE_TIME,
            description: 'Send this back as If-Modified-Since on the next poll to get a 304 while nothing changed.'
        }
    }
};

const COMPLETION_LINE = {
    type: 'object',
    properties: {
        sku: { ...NULLABLE_STRING, description: 'Null on a surplus entry.' },
        status: { type: 'string', enum: ['complete', 'partial', 'missing', 'over'] },
        expectedQty: COUNT,
        scannedQty: COUNT,
        tagIds: {
            type: 'array',
            items: { type: 'string' },
            description: 'RFID tags verified on this line. Absent, not empty, on a missing line.'
        },
        assetNos: {
            type: 'array',
            items: { type: 'string' },
            description: 'Asset numbers verified on this line — the identifier to use for barcode-only items.'
        }
    }
};

const COMPLETION_RESPONSE = {
    type: 'object',
    properties: {
        orderTitle: NULLABLE_STRING,
        orderStatus: { ...NULLABLE_STRING, description: 'Closed, Closed-Short or Closed-Over.' },
        closedTime: NULLABLE_DATE_TIME,
        closedReason: NULLABLE_STRING,
        variance: {
            type: 'object',
            properties: { expected: COUNT, verified: COUNT, short: COUNT, over: COUNT }
        },
        lines: {
            type: 'array',
            items: COMPLETION_LINE,
            description:
                'Every expected SKU, including ones nothing arrived for (status "missing"), plus surplus entries ' +
                '(sku null, status "over").'
        }
    }
};

const COPY_RESPONSE = {
    type: 'object',
    properties: {
        title: { type: 'string', description: 'The replacement order, under the original title.' },
        archivedTitle: { type: 'string', description: 'What the retired original was renamed to, e.g. ORD-001-old1.' },
        order: { type: 'object', description: 'The replacement order record.' }
    }
};

const json = schema => ({ 'application/json': { schema } });

function buildOrderConfirmationPaths(tags) {
    return {
        [`${ORDER_BASE_PATH}/{title}/status`]: {
            get: {
                tags,
                summary: 'status',
                description:
                    'Live progress of one order, by title. Built for polling once a second: the body is a fixed size and ' +
                    'never contains the item list. Counts are units, not rows.',
                operationId: 'get_order_order_status',
                parameters: [
                    TITLE_PARAMETER,
                    {
                        name: 'If-Modified-Since',
                        in: 'header',
                        required: false,
                        description: "The previous response's lastModified. Answered 304 while the order is unchanged.",
                        schema: { type: 'string' }
                    }
                ],
                responses: {
                    200: { description: 'Current state of the order.', content: json(STATUS_RESPONSE) },
                    304: { description: 'Unchanged since If-Modified-Since.' },
                    403: { description: 'auth-required' },
                    404: { description: 'No order with that title.' }
                }
            }
        },
        [`${ORDER_BASE_PATH}/{title}/complete`]: {
            post: {
                tags,
                summary: 'complete',
                description:
                    'Close the order and return its reconciliation: the variance, and the verified tag ids in place of the ' +
                    'expected SKUs. The closing status is derived — surplus closes Closed-Over, outstanding units ' +
                    'Closed-Short, an exact match Closed. Safe to repeat: a repeat returns the same body with 200 and does ' +
                    'not close the order again. The request body is empty.',
                operationId: 'post_order_order_complete',
                parameters: [TITLE_PARAMETER],
                responses: {
                    201: { description: 'Closed by this call.', content: json(COMPLETION_RESPONSE) },
                    200: { description: 'Already closed: the same answer, nothing written.', content: json(COMPLETION_RESPONSE) },
                    403: { description: 'auth-required' },
                    404: { description: 'No order with that title.' },
                    409: {
                        description:
                            'The order cannot be closed in its current status (permanent), or a completion already in ' +
                            'flight had not landed within a few seconds (retry shortly; the message says which).'
                    }
                }
            }
        },
        [`${ORDER_BASE_PATH}/{title}/copy`]: {
            post: {
                tags,
                summary: 'copy',
                description:
                    'Recreate a failed order: the original is retired and kept for reporting, renamed <title>-old1 ' +
                    '(then -old2…), and a clean replacement with reset quantities is created under the original title. ' +
                    'Only a Closed, Closed-Short, Closed-Over or Suspended order can be recreated. The request body is empty.',
                operationId: 'post_order_order_copy',
                parameters: [TITLE_PARAMETER],
                responses: {
                    201: { description: 'The replacement was created.', content: json(COPY_RESPONSE) },
                    403: { description: 'auth-required' },
                    404: { description: 'No order with that title.' },
                    409: { description: 'The order cannot be recreated in its current status, or another request retired it first.' }
                }
            }
        }
    };
}

/**
 * Add the Order Confirmation endpoints to a generated paths object, when the tenant has the feature
 * and the reader can see the order API at all. Mutates and returns `paths`.
 *
 * @param {object} paths - the generated OpenAPI paths
 * @param {{ tenantSettings?: object }} options
 * @returns {object} paths
 */
function addOrderConfirmationPaths(paths, { tenantSettings } = {}) {
    if (!tenantSettings?.orderConfirmation_enabled) return paths;

    const anchor = paths[ORDER_BASE_PATH] || paths[`${ORDER_BASE_PATH}/{id}`];
    if (!anchor) return paths;

    const tags = Object.values(anchor).find(operation => Array.isArray(operation?.tags))?.tags || ['order'];
    return Object.assign(paths, buildOrderConfirmationPaths(tags));
}

module.exports = { addOrderConfirmationPaths };
